import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';

interface RestoreLocationsModalProps {
  open: boolean;
  onClose: () => void;
  // Called after a successful restore so the caller can refresh its own list.
  onRestored: () => void;
}

const ALL_TYPES = 'all';

const RestoreLocationsModalContent = ({
  open,
  onClose,
  onRestored,
}: RestoreLocationsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const locationApi = useRef(new LocationImporterAPI()).current;

  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deletedLocations, setDeletedLocations] = useState<ImportedLocation[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );

  const toast = (message: string, type: 'success' | 'error') => {
    dispatch(showNotification({ message, type }));
  };

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const data = await locationApi.getDeletedLocations();
      setDeletedLocations(data ?? []);
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('loc_importer_restore_toast_load_failed'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [locationApi]);

  useEffect(() => {
    if (!open) return;
    setSearchQuery('');
    setTypeFilter(ALL_TYPES);
    setSelectionModel([]);
    void fetchDeleted();
  }, [open, fetchDeleted]);

  const locationTypes = useMemo(
    () =>
      Array.from(
        new Set(
          deletedLocations
            .map((loc) => loc.locationType)
            .filter((type): type is string => Boolean(type))
        )
      ).sort((a, b) => a.localeCompare(b)),
    [deletedLocations]
  );

  const filteredLocations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return deletedLocations.filter((loc) => {
      if (q && !loc.name?.toLowerCase().includes(q)) return false;
      if (
        typeFilter !== ALL_TYPES &&
        loc.locationType?.toLowerCase() !== typeFilter.toLowerCase()
      )
        return false;
      return true;
    });
  }, [deletedLocations, searchQuery, typeFilter]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('loc_importer_col_name'),
        flex: 1.5,
        minWidth: 140,
      },
      {
        field: 'locationType',
        headerName: t('loc_importer_col_type'),
        flex: 1,
        minWidth: 110,
      },
    ],
    [t]
  );

  const handleRestore = async () => {
    if (selectionModel.length === 0) return;
    setRestoring(true);
    try {
      await locationApi.restoreLocations(selectionModel.map(String));
      toast(
        t('loc_importer_restore_toast_success', {
          count: selectionModel.length,
        }),
        'success'
      );
      setSelectionModel([]);
      onRestored();
      await fetchDeleted();
    } catch (e) {
      toast(
        e instanceof Error ? e.message : t('loc_importer_restore_toast_failed'),
        'error'
      );
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: (th: Theme) => alpha(th.palette.divider, 0.4),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th: Theme) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th: Theme) => alpha(th.palette.primary.main, 0.3),
              flexShrink: 0,
            }}
          >
            <RestoreFromTrashOutlinedIcon
              sx={{ fontSize: 18, color: 'primary.main' }}
            />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('loc_importer_restore_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('loc_importer_restore_subtitle')}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            columnGap: 1.5,
            rowGap: 3,
            mt: 2,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder={t('loc_importer_restore_search_placeholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Select
            size="small"
            value={typeFilter}
            onChange={(e: SelectChangeEvent) => {
              setTypeFilter(e.target.value);
            }}
            displayEmpty
            sx={{ minWidth: 180 }}
          >
            <MenuItem value={ALL_TYPES}>
              {t('loc_importer_restore_filter_type_all')}
            </MenuItem>
            {locationTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ height: 380, position: 'relative' }}>
          {loading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <CustomStyledDataGrid
              rows={filteredLocations}
              columns={columns}
              getRowId={(row) => (row as ImportedLocation).locationId}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
              hideFooter={false}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              localeText={{
                noRowsLabel: t('loc_importer_restore_empty'),
              }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (th: Theme) => alpha(th.palette.divider, 0.4),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('loc_importer_cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={selectionModel.length === 0 || restoring}
          onClick={() => {
            void handleRestore();
          }}
          startIcon={
            restoring ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RestoreFromTrashOutlinedIcon />
            )
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('loc_importer_restore_confirm_btn', {
            count: selectionModel.length,
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const RestoreLocationsModal = (props: RestoreLocationsModalProps) => (
  <CustomMuiThemeProvider>
    <RestoreLocationsModalContent {...props} />
  </CustomMuiThemeProvider>
);
